import { useState, useMemo } from 'react';
import { X } from 'lucide-react';
import { MultiSelectPosEntity } from '@/components/common/MultiSelectPosEntity';
import { ManageItemsModal } from './ManageItemsModal';
import type { SyncedDiscount, DiscountOptions } from '@/types/api';

interface DiscountFormProps {
  formData: Partial<SyncedDiscount>;
  onChange: (data: Partial<SyncedDiscount>) => void;
  options?: DiscountOptions;
}

export function DiscountForm({ formData, onChange, options }: DiscountFormProps) {
  const [isManageItemsOpen, setIsManageItemsOpen] = useState(false);

  // Build a posId → name lookup for selected items display
  const itemNameMap = useMemo(() => {
    if (!options) return new Map<number, string>();
    const map = new Map<number, string>();
    for (const item of options.menuItems) {
      map.set(item.posId, item.name);
    }
    return map;
  }, [options]);

  const selectedItemIds = formData.menuItemIds || [];

  const removeItem = (posId: number) => {
    onChange({ ...formData, menuItemIds: selectedItemIds.filter(id => id !== posId) });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
        <input
          type="text"
          value={formData.name || ''}
          onChange={(e) => onChange({ ...formData, name: e.target.value })}
          className="input w-full"
          required
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
          <select
            value={formData.type ?? ''}
            onChange={(e) => onChange({ ...formData, type: e.target.value ? parseInt(e.target.value) : undefined })}
            className="input w-full"
          >
            <option value="">Select...</option>
            <option value="0">Amount</option>
            <option value="1">Percentage</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Value</label>
          <input
            type="number"
            step="0.01"
            value={formData.value ?? ''}
            onChange={(e) => onChange({ ...formData, value: e.target.value ? parseFloat(e.target.value) : undefined })}
            className="input w-full"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Qualification Type</label>
          <select
            value={formData.qualificationType ?? ''}
            onChange={(e) => onChange({ ...formData, qualificationType: e.target.value ? parseInt(e.target.value) : undefined })}
            className="input w-full"
          >
            <option value="">Select...</option>
            <option value="0">Item</option>
            <option value="1">Order</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Min. Order Value</label>
          <input
            type="number"
            step="0.01"
            value={formData.minimumBuy ?? ''}
            onChange={(e) => onChange({ ...formData, minimumBuy: e.target.value ? parseFloat(e.target.value) : undefined })}
            className="input w-full"
          />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Min Quantity</label>
          <input
            type="number"
            value={formData.minQuantity ?? ''}
            onChange={(e) => onChange({ ...formData, minQuantity: e.target.value ? parseInt(e.target.value) : undefined })}
            className="input w-full"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Max Value Per Guest</label>
          <input
            type="number"
            step="0.01"
            value={formData.maxValuePerGuest ?? ''}
            onChange={(e) => onChange({ ...formData, maxValuePerGuest: e.target.value ? parseFloat(e.target.value) : undefined })}
            className="input w-full"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Barcode</label>
        <input
          type="text"
          value={formData.barcode || ''}
          onChange={(e) => onChange({ ...formData, barcode: e.target.value || undefined })}
          className="input w-full"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Expiry Date</label>
        <input
          type="text"
          value={formData.expiryDate || ''}
          onChange={(e) => onChange({ ...formData, expiryDate: e.target.value || undefined })}
          className="input w-full"
          placeholder="YYYY-MM-DD"
        />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={formData.isEnabled ?? true}
            onChange={(e) => onChange({ ...formData, isEnabled: e.target.checked })}
            className="rounded border-gray-300"
          />
          <span className="text-sm text-gray-700">Enabled</span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={formData.isAutoApply ?? false}
            onChange={(e) => onChange({ ...formData, isAutoApply: e.target.checked })}
            className="rounded border-gray-300"
          />
          <span className="text-sm text-gray-700">Auto Apply</span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={formData.isAutoApplyAfterSelection ?? false}
            onChange={(e) => onChange({ ...formData, isAutoApplyAfterSelection: e.target.checked })}
            className="rounded border-gray-300"
          />
          <span className="text-sm text-gray-700">Auto Apply After Selection</span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={formData.isNeverExpire ?? true}
            onChange={(e) => onChange({ ...formData, isNeverExpire: e.target.checked })}
            className="rounded border-gray-300"
          />
          <span className="text-sm text-gray-700">Never Expire</span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={formData.isOnlyFood ?? false}
            onChange={(e) => onChange({ ...formData, isOnlyFood: e.target.checked })}
            className="rounded border-gray-300"
          />
          <span className="text-sm text-gray-700">Only Food</span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={formData.isOnlyDrinks ?? false}
            onChange={(e) => onChange({ ...formData, isOnlyDrinks: e.target.checked })}
            className="rounded border-gray-300"
          />
          <span className="text-sm text-gray-700">Only Drinks</span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={formData.isWebsiteOnly ?? false}
            onChange={(e) => onChange({ ...formData, isWebsiteOnly: e.target.checked })}
            className="rounded border-gray-300"
          />
          <span className="text-sm text-gray-700">Website Only</span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={formData.isApplyToDeliveryCharge ?? false}
            onChange={(e) => onChange({ ...formData, isApplyToDeliveryCharge: e.target.checked })}
            className="rounded border-gray-300"
          />
          <span className="text-sm text-gray-700">Apply To Delivery Charge</span>
        </label>
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={formData.isApplyOnLast ?? false}
            onChange={(e) => onChange({ ...formData, isApplyOnLast: e.target.checked })}
            className="rounded border-gray-300"
          />
          <span className="text-sm text-gray-700">Apply On Last</span>
        </label>
      </div>

      {/* Restrictions */}
      {options && (
        <div className="space-y-4 border-t pt-4 mt-4">
          <h3 className="text-sm font-semibold text-gray-700">Restrictions</h3>

          <MultiSelectPosEntity
            label="Order Types (empty = all)"
            options={options.orderTypes}
            selectedIds={formData.orderTypeIds || []}
            onChange={(ids) => onChange({ ...formData, orderTypeIds: ids })}
          />

          <MultiSelectPosEntity
            label="Shifts (empty = all)"
            options={options.shifts}
            selectedIds={formData.shiftIds || []}
            onChange={(ids) => onChange({ ...formData, shiftIds: ids })}
          />

          {/* Menu Items section */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-semibold text-gray-700">
                Menu Items
                {selectedItemIds.length > 0 && (
                  <span className="ml-1 text-xs font-normal text-gray-500">
                    ({selectedItemIds.length} selected)
                  </span>
                )}
              </h3>
              <button
                type="button"
                onClick={() => setIsManageItemsOpen(true)}
                className="btn btn-outline text-xs px-3 py-1"
              >
                Manage Items
              </button>
            </div>

            {selectedItemIds.length === 0 ? (
              <p className="text-sm text-gray-400 italic">No items selected (applies to all)</p>
            ) : (
              <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto border rounded p-2">
                {selectedItemIds.map(id => (
                  <span
                    key={id}
                    className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded-full"
                  >
                    {itemNameMap.get(id) || `#${id}`}
                    <button
                      type="button"
                      onClick={() => removeItem(id)}
                      className="ml-0.5 p-0.5 rounded-full text-red-500 hover:text-red-700 hover:bg-red-100"
                    >
                      <X className="w-4 h-4" strokeWidth={3} />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          <ManageItemsModal
            isOpen={isManageItemsOpen}
            onClose={() => setIsManageItemsOpen(false)}
            options={options}
            selectedItemIds={selectedItemIds}
            onChange={(ids) => onChange({ ...formData, menuItemIds: ids })}
          />
        </div>
      )}
    </div>
  );
}
